// components/modals/EndowmentModals/AddToEndowmentModal.jsx - Enhanced with better metadata handling
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { removeTxtExtension } from "../../../services/inheritance.service";
import { fetchFileContent } from "../../../services/api.service";
import { useAuth } from "../../../contexts/AuthContext";
import { CONTENT_ENDPOINTS, STREAM_ENDPOINTS } from "../../../utils/constants";
import "../../../styles/components/addToEndowmentModal.css";
import useModalHistory from "../../../hooks/useModalHistory";

/**
 * Modal for adding items to an endowment
 */
export const AddToEndowmentModal = ({
  show,
  onHide,
  currentPath,
  onNavigate,
  onBack,
  searchTerm,
  onSearchChange,
  filterOption,
  onFilterChange,
  onConfirm,
  isLoading,
  endowment,
  selectedItems,
  setSelectedItems,
  onViewItem,
}) => {
  useModalHistory(show, onHide, "endowment-add-modal");

  const handleFilesFolderClick = (folderName) => {
    setFilesCurrentPath((prev) => [...prev, folderName]);
  };

  // Handle back navigation within Files
  const handleFilesBackClick = () => {

    if (filesCurrentPath.length > 0) {
      setFilesCurrentPath((prev) => {
        const newPath = prev.slice(0, -1);
        return newPath;
      });

      // Force refresh after navigation
      setTimeout(() => {
        loadItems();
      }, 100);
    } else {
      onBack();
    }
  };

  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]); // Store all items for filtering
  const [contentLoading, setContentLoading] = useState(true);
  const [savingItems, setSavingItems] = useState(false);
  const [filesCurrentPath, setFilesCurrentPath] = useState([]);

  // Load items when modal opens or path changes
  useEffect(() => {
    if (show) {
      loadItems();
    }
  }, [show, currentPath, filesCurrentPath]);

  // Apply search and filter when search term or filter option changes
  useEffect(() => {
    if (show && currentPath.length > 0 && allItems.length > 0) {
      applySearchAndFilter();
    }
  }, [searchTerm, filterOption, allItems]);

  useEffect(() => {
    if (show && currentPath.length > 0 && endowment) {
      loadItems();
    }
  }, [endowment?.selectedItems, show]);

  // Load items for the current path
  const loadItems = async () => {
    if (!show) return;

    setContentLoading(true);
    try {
      if (currentPath.length === 0) {
        // Show categories
        const categoryItems = [
          { id: 1, name: "Files", type: "category" },
          { id: 2, name: "Passwords", type: "category" },
          { id: 3, name: "Banking", type: "category" },
          { id: 4, name: "Blockchain", type: "category" },
        ];
        setItems(categoryItems);
        setAllItems(categoryItems);
      } else {
        // Fetch items for the selected category
        const category = currentPath[currentPath.length - 1];
        await fetchCategoryItems(category);
      }
    } catch (error) {
      console.error("Error loading items:", error);
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch items for a category
  const fetchCategoryItems = async (category) => {
    try {
      let bucket;

      switch (category.toLowerCase()) {
        case "files":
          bucket = "files";

          break;
        case "passwords":
          bucket = "passwords";
          break;
        case "banking":
          bucket = "bankcards";
          break;
        case "blockchain":
          bucket = "blockchain";
          break;
        default:
          throw new Error("Invalid category");
      }

      const response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ bucket }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result || !result.filesMetadata) {
        setAllItems([]);
        setItems([]);
        return;
      }

      let fetchedItems = [];

      // For files category, handle folder navigation
      if (category.toLowerCase() === "files") {
        if (filesCurrentPath.length === 0) {
          // Add individual files
          if (result.filesMetadata) {
            const rootFiles = result.filesMetadata.map((file, index) => ({
              id: `file-${index}`,
              name: file.Key,
              type: "file",
              size: file.Size || 0,
              lastModified: file.LastModified,
              lastModifiedDate: new Date(file.LastModified),
              contentType: getContentTypeFromFileName(file.Key),
              etag: file.ETag,
              storageClass: file.StorageClass,
            }));
            fetchedItems.push(...rootFiles);
          }

          // Add folders
          if (result.folders) {
            const rootFolders = await Promise.all(
              result.folders.map(async (folder, index) => {
                const folderPrefix = folder.Prefix;

                try {
                  // Fetch actual folder contents to get total file count
                  const folderResponse = await fetch(
                    CONTENT_ENDPOINTS.FOLDER_CONTENT,
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        bucket: "files",
                        folderId: folderPrefix.endsWith("/")
                          ? folderPrefix
                          : `${folderPrefix}/`,
                      }),
                    }
                  );

                  let allFilesInFolder = [];
                  if (folderResponse.ok) {
                    const folderData = await folderResponse.json();
                    // Get all file paths in this folder (recursively)
                    allFilesInFolder = getAllFilePathsFromFolder(
                      folderData.items || [],
                      folderPrefix
                    );
                  }

                  // Find which of these files are in the endowment
                  const endowmentFiles = endowment?.selectedItems?.files || [];
                  const filesInEndowment = allFilesInFolder.filter((filePath) =>
                    endowmentFiles.includes(filePath)
                  );

                  return {
                    id: `folder-${index}`,
                    name: folderPrefix,
                    type: "folder",
                    fullPath: folderPrefix,
                    fileCount: allFilesInFolder.length, // Total files in folder
                    files: allFilesInFolder, // ALL files in folder
                    filesInEndowment: filesInEndowment, // Files currently in endowment
                    size: "Folder",
                    lastModified: new Date().toISOString(),
                  };
                } catch (error) {
                  console.error(
                    `Error fetching contents for folder ${folderPrefix}:`,
                    error
                  );
                  return {
                    id: `folder-${index}`,
                    name: folderPrefix,
                    type: "folder",
                    fullPath: folderPrefix,
                    fileCount: 0,
                    files: [],
                    filesInEndowment: [],
                    size: "Folder",
                    lastModified: new Date().toISOString(),
                  };
                }
              })
            );

            fetchedItems.push(...rootFolders);
          }

        } else {
          // In a subfolder - need to fetch folder contents
          const currentFolderPath = filesCurrentPath.join("/");

          // We need to make a separate API call to get folder contents
          await fetchFolderContents(currentFolderPath, fetchedItems);
          return; // fetchFolderContents will handle setting the items
        }
      } else {
        // For other categories (passwords, banking, blockchain), use filesMetadata as before
        if (result.filesMetadata) {
          fetchedItems = result.filesMetadata.map((file, index) => ({
            id: index + 1,
            name: file.Key,
            type: category.toLowerCase(),
            size: file.Size || 0,
            lastModified: file.LastModified,
            lastModifiedDate: new Date(file.LastModified),
            contentType: getContentTypeFromFileName(file.Key),
            etag: file.ETag,
            storageClass: file.StorageClass,
          }));
        }
      }

      // Store all items for filtering
      setAllItems(fetchedItems);

      // Apply initial search and filter
      applySearchAndFilterToItems(fetchedItems);
    } catch (error) {
      console.error("Error fetching category items:", error);
      setAllItems([]);
      setItems([]);
    }
  };

  const getAllFilePathsFromFolder = (items, folderPrefix) => {
    const filePaths = [];

    items.forEach((item) => {
      if (item.type === "file") {
        // Add the full path of the file
        const filePath = item.path || item.name;
        filePaths.push(filePath);
      }
      // Note: We're not recursing into subfolders for now since that would be expensive
      // If you need recursive folder support, we'd need to make additional API calls
    });

    return filePaths;
  };

  const fetchFolderContents = async (folderPath, targetArray) => {
    try {
      // Use the FOLDER_CONTENT endpoint similar to the main Files component
      const response = await fetch(CONTENT_ENDPOINTS.FOLDER_CONTENT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bucket: "files",
          folderId: folderPath.endsWith("/") ? folderPath : `${folderPath}/`,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result || !result.items) {
        setAllItems([]);
        setItems([]);
        return;
      }

      // Process folder contents
      const folderItems = result.items.map((item, index) => {
        if (item.type === "folder") {
          // For subfolders, find files in endowment that belong to this folder
          const endowmentFiles = endowment?.selectedItems?.files || [];
          const fullFolderPath = item.path || item.Prefix;
          const filesInThisFolder = endowmentFiles.filter((filePath) =>
            filePath.startsWith(fullFolderPath + "/")
          );

          return {
            id: `folder-${index}`,
            name:
              item.name ||
              (item.Prefix
                ? item.Prefix.split("/").filter(Boolean).pop()
                : "Unnamed"),
            type: "folder",
            fullPath: fullFolderPath,
            fileCount: filesInThisFolder.length,
            files: filesInThisFolder,
            size: "Folder",
            lastModified: item.lastModified || new Date().toISOString(),
          };
        } else {
          // For files, check if this specific file is in endowment
          const endowmentFiles = endowment?.selectedItems?.files || [];
          const fullFilePath = item.path || item.Key;

          return {
            id: `file-${index}`,
            name:
              item.name || (item.Key ? item.Key.split("/").pop() : "Unnamed"),
            type: "file",
            size: item.size || item.Size || 0,
            lastModified:
              item.lastModified ||
              item.LastModified ||
              new Date().toISOString(),
            lastModifiedDate: new Date(
              item.lastModified || item.LastModified || new Date()
            ),
            contentType: getContentTypeFromFileName(
              item.name || item.Key || ""
            ),
            fullPath: fullFilePath,
          };
        }
      });

      setAllItems(folderItems);
      applySearchAndFilterToItems(folderItems);
    } catch (error) {
      console.error("Error fetching folder contents:", error);
      setAllItems([]);
      setItems([]);
    }
  };

  // Helper function to get content type from file name
  const getContentTypeFromFileName = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    const mimeTypes = {
      // Images
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      bmp: "image/bmp",
      svg: "image/svg+xml",
      webp: "image/webp",

      // Documents
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",

      // Text
      txt: "text/plain",
      html: "text/html",
      css: "text/css",
      js: "text/javascript",
      jsx: "text/javascript",
      ts: "text/typescript",
      tsx: "text/typescript",
      json: "application/json",
      xml: "text/xml",
      md: "text/markdown",

      // Audio
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
      flac: "audio/flac",

      // Video
      mp4: "video/mp4",
      avi: "video/avi",
      mov: "video/quicktime",
      wmv: "video/x-ms-wmv",
      mkv: "video/x-matroska",
      webm: "video/webm",

      // Archives
      zip: "application/zip",
      rar: "application/x-rar-compressed",
      "7z": "application/x-7z-compressed",
    };

    return mimeTypes[extension] || "application/octet-stream";
  };

  // Apply search and filter to current allItems
  const applySearchAndFilter = () => {
    applySearchAndFilterToItems(allItems);
  };

  // Apply search and filter to provided items array
  const applySearchAndFilterToItems = (itemsToFilter) => {
    let filteredItems = [...itemsToFilter];

    // Apply search
    if (searchTerm && searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      filteredItems = filteredItems.filter((item) =>
        removeTxtExtension(item.name).toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Apply filter/sort
    switch (filterOption) {
      case "name-asc":
        filteredItems.sort((a, b) =>
          removeTxtExtension(a.name).localeCompare(removeTxtExtension(b.name))
        );
        break;
      case "name-desc":
        filteredItems.sort((a, b) =>
          removeTxtExtension(b.name).localeCompare(removeTxtExtension(a.name))
        );
        break;
      case "date-asc":
        filteredItems.sort((a, b) => {
          const dateA = a.lastModifiedDate || new Date(a.lastModified || 0);
          const dateB = b.lastModifiedDate || new Date(b.lastModified || 0);
          return dateA - dateB; // Oldest first
        });
        break;
      case "date-desc":
        filteredItems.sort((a, b) => {
          const dateA = a.lastModifiedDate || new Date(a.lastModified || 0);
          const dateB = b.lastModifiedDate || new Date(b.lastModified || 0);
          return dateB - dateA; // Newest first
        });
        break;
      case "size-asc":
        filteredItems.sort((a, b) => {
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          return sizeA - sizeB; // Smallest first
        });
        break;
      case "size-desc":
        filteredItems.sort((a, b) => {
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          return sizeB - sizeA; // Largest first
        });
        break;
      case "all":
      default:
        // Default sorting (by name ascending)
        filteredItems.sort((a, b) =>
          removeTxtExtension(a.name).localeCompare(removeTxtExtension(b.name))
        );
    }

    setItems(filteredItems);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  const getCategoryKey = (category) => {
    const mapping = {
      banking: "bankcards",
      blockchain: "blockchain",
      files: "files",
      passwords: "passwords",
    };
    return mapping[category.toLowerCase()] || category;
  };

  // Handle view item - pass to parent component
  const handleViewItem = async (item) => {

    if (!onViewItem) {
      console.error('onViewItem prop not provided');
      return;
    }

    const category = currentPath[currentPath.length - 1];

    // For files category, ensure we have the correct full path
    if (category.toLowerCase() === 'files' && item.type === 'file') {
      let fullPath = item.fullPath || item.name;

      // If we're in a subfolder and the item doesn't have fullPath, construct it
      if (filesCurrentPath.length > 0 && !item.fullPath) {
        fullPath = `${filesCurrentPath.join('/')}/${item.name}`;
      }

      // Create enhanced item with correct path
      const enhancedItem = {
        ...item,
        fullPath: fullPath
      };

      onViewItem(enhancedItem.fullPath || enhancedItem, category);
    } else {
      // For non-files or folders, pass as-is
      onViewItem(item, category);
    }
  };

  const handleItemSelect = async (item, checked) => {
    const category = currentPath[currentPath.length - 1].toLowerCase();
    const newSelectedItems = { ...selectedItems };

    // Initialize the array if it doesn't exist
    if (!newSelectedItems[category]) {
      newSelectedItems[category] = [];
    }

    if (item.type === 'folder') {
      if (checked) {
        // Fetch ALL files in the folder recursively from the server
        try {

          const allFilesInFolder = await getAllFilesInFolderRecursively(item.fullPath || item.name);

          // Add all files from folder that aren't already selected
          allFilesInFolder.forEach(filePath => {
            if (!newSelectedItems[category].includes(filePath)) {
              newSelectedItems[category].push(filePath);
            }
          });

        } catch (error) {
          console.error('Error fetching folder contents:', error);
          // Fallback to using the files we know about
          const filesInFolder = item.files || [];
          filesInFolder.forEach(filePath => {
            if (!newSelectedItems[category].includes(filePath)) {
              newSelectedItems[category].push(filePath);
            }
          });
        }
      } else {
        // When unchecking, we need to get all files that were in the folder to remove them
        try {
          const allFilesInFolder = await getAllFilesInFolderRecursively(item.fullPath || item.name);

          // Remove all files from folder
          allFilesInFolder.forEach(filePath => {
            const index = newSelectedItems[category].indexOf(filePath);
            if (index !== -1) {
              newSelectedItems[category].splice(index, 1);
            }
          });
        } catch (error) {
          console.error('Error fetching folder contents for removal:', error);
          // Fallback to using the files we know about
          const filesInFolder = item.files || [];
          filesInFolder.forEach(filePath => {
            const index = newSelectedItems[category].indexOf(filePath);
            if (index !== -1) {
              newSelectedItems[category].splice(index, 1);
            }
          });
        }
      }
    } else {
      // Regular file selection logic remains the same
      let filePath;

      if (filesCurrentPath.length > 0) {
        filePath = `${filesCurrentPath.join('/')}/${item.name}`;
      } else {
        filePath = item.name;
      }

      const fullFilePath = item.fullPath || filePath;

      if (checked) {
        if (!newSelectedItems[category].includes(fullFilePath)) {
          newSelectedItems[category].push(fullFilePath);
        }
      } else {
        const index = newSelectedItems[category].indexOf(fullFilePath);
        if (index !== -1) {
          newSelectedItems[category].splice(index, 1);
        }
      }
    }

    setSelectedItems(newSelectedItems);
  };

  const getAllFilesInFolderRecursively = async (folderPath) => {
    const allFiles = [];

    try {

      // Fetch the folder contents from the server
      const response = await fetch(CONTENT_ENDPOINTS.FOLDER_CONTENT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucket: 'files',
          folderId: folderPath.endsWith('/') ? folderPath : `${folderPath}/`
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result && result.items) {
        for (const item of result.items) {
          if (item.type === 'file') {
            // Add file to the list
            const filePath = item.path || item.Key || `${folderPath}/${item.name}`;
            allFiles.push(filePath);
          } else if (item.type === 'folder') {
            // Recursively get files from subfolder
            const subfolderPath = item.path || item.Prefix || `${folderPath}/${item.name}`;
            const subfolderFiles = await getAllFilesInFolderRecursively(subfolderPath);
            allFiles.push(...subfolderFiles);
          }
        }
      }

    } catch (error) {
      console.error(`Error fetching recursive contents for ${folderPath}:`, error);
      throw error;
    }

    return allFiles;
  };

  // Handle category click
  const handleCategoryClick = (category) => {
    onNavigate([...currentPath, category]);
  };

  // Handle save
  const handleSave = async () => {
    setSavingItems(true);
    try {
      await onConfirm(selectedItems);
    } finally {
      setSavingItems(false);
    }
  };

  // Get item icon details
  const getItemIconDetails = (item, category) => {
    let iconClass, iconColor;


    // Handle item object vs string
    const itemName = typeof item === "string" ? item : item.name;
    const itemType = typeof item === "object" ? item.type : "file";

    switch (category?.toLowerCase()) {
      case "folder":
        iconClass = "fa-folder";
        iconColor = "#ffd700";
        break;
      case "file":
        // Check if it's a folder first
        if (itemType === "folder") {
          iconClass = "fa-folder";
          iconColor = "#ffd700";
        } else {
          // Determine file type icon based on extension
          const extension = itemName.split(".").pop()?.toLowerCase() || "";
          if (
            ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)
          ) {
            iconClass = "fa-file-image";
          } else if (["doc", "docx"].includes(extension)) {
            iconClass = "fa-file-word";
          } else if (["xls", "xlsx"].includes(extension)) {
            iconClass = "fa-file-excel";
          } else if (["pdf"].includes(extension)) {
            iconClass = "fa-file-pdf";
          } else if (["mp4", "avi", "mov", "mkv", "webm"].includes(extension)) {
            iconClass = "fa-file-video";
          } else if (["mp3", "wav", "ogg", "flac"].includes(extension)) {
            iconClass = "fa-file-audio";
          } else if (["zip", "rar", "7z"].includes(extension)) {
            iconClass = "fa-file-archive";
          } else if (
            [
              "txt",
              "md",
              "html",
              "css",
              "js",
              "jsx",
              "ts",
              "tsx",
              "json",
              "xml",
            ].includes(extension)
          ) {
            iconClass = "fa-file-code";
          } else {
            iconClass = "fa-file-alt";
          }
          iconColor = "#ffd700";
        }
        break;

      case "passwords":
        iconClass = "fa-key";
        iconColor = "#ffd700";
        break;

      case "banking":
        iconClass = "fa-credit-card";
        iconColor = "#00CC5C";
        break;

      case "blockchain":
        iconClass = "fa-link";
        iconColor = "#4B0082";
        break;

      case "category":
        iconClass = "fa-folder";
        iconColor = "#ffd700";
        break;

      default:
        iconClass = "fa-file";
        iconColor = "#000000";
    }

    return { iconClass, iconColor };
  };

  // Check if item is already in endowment
  const isItemInEndowment = (item) => {
    if (!endowment || !item) return false;

    const category = currentPath[currentPath.length - 1]?.toLowerCase();
    if (!category) return false;

    const endowmentItems = endowment.selectedItems?.[category] || [];

    if (item.type === "folder") {
      // For folders, check if ALL files in the folder are already in endowment
      const filesInFolder = item.files || [];
      if (filesInFolder.length === 0) return false;

      return filesInFolder.every((filePath) =>
        endowmentItems.includes(filePath)
      );
    } else {
      // For files, check both the name and fullPath
      const filePath = item.fullPath || item.name;
      return (
        endowmentItems.includes(filePath) || endowmentItems.includes(item.name)
      );
    }
  };

  const isFolderPartiallyInEndowment = (item) => {
    if (!endowment || !item || item.type !== "folder") return false;

    const category = currentPath[currentPath.length - 1]?.toLowerCase();
    if (!category) return false;

    const endowmentItems = endowment.selectedItems?.[category] || [];
    const filesInFolder = item.files || [];

    if (filesInFolder.length === 0) return false;

    const filesInEndowment = filesInFolder.filter((filePath) =>
      endowmentItems.includes(filePath)
    );

    // Some files are in endowment, but not all
    return (
      filesInEndowment.length > 0 &&
      filesInEndowment.length < filesInFolder.length
    );
  };

  const isItemSelected = (item) => {
    const category = currentPath[currentPath.length - 1]?.toLowerCase();
    if (!category) return false;

    if (item.type === 'folder') {
      // For folders, we need to check if files are selected, but we might not have 
      // the complete list yet. This is a simplified check.
      const filesInFolder = item.files || [];
      const selectedFiles = selectedItems[category] || [];

      if (filesInFolder.length === 0) {
        // If we don't know the files yet, check if any files with this folder prefix are selected
        const folderPrefix = (item.fullPath || item.name) + '/';
        return selectedFiles.some(filePath => filePath.startsWith(folderPrefix));
      }

      return filesInFolder.every(filePath => selectedFiles.includes(filePath));
    } else {
      // Regular file check remains the same
      let filePath;

      if (filesCurrentPath.length > 0) {
        filePath = `${filesCurrentPath.join('/')}/${item.name}`;
      } else {
        filePath = item.name;
      }

      const fullFilePath = item.fullPath || filePath;
      return (selectedItems[category] || []).includes(fullFilePath);
    }
  };

  // Count selected items
  const countSelectedItems = () => {
    let total = 0;
    Object.values(selectedItems).forEach((items) => {
      total += items.length;
    });
    return total;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
      className="common-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {currentPath.length === 0
            ? "Add Items to Endowment"
            : `Add ${currentPath[currentPath.length - 1]} to Endowment`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Breadcrumb Navigation */}
        <div className="aem-breadcrumb-nav d-flex">
          <Button
            variant="link"
            onClick={
              currentPath.length > 0 &&
                currentPath[currentPath.length - 1].toLowerCase() === "files" &&
                filesCurrentPath.length > 0
                ? handleFilesBackClick
                : onBack
            }
            disabled={currentPath.length === 0}
            className="aem-back-button"
          >
            <i className="fas fa-arrow-left mr-1"></i> Back
          </Button>

          <nav aria-label="breadcrumb" className="flex-grow-1">
            <ol className="breadcrumb aem-breadcrumb">
              <li
                className="breadcrumb-item aem-breadcrumb-item"
                onClick={() => onNavigate([])}
              >
                Add Items
              </li>

              {currentPath.map((item, index) => (
                <li
                  key={index}
                  className={`breadcrumb-item aem-breadcrumb-item ${index === currentPath.length - 1 &&
                      filesCurrentPath.length === 0
                      ? "active"
                      : ""
                    }`}
                  style={{
                    cursor:
                      index === currentPath.length - 1 &&
                        filesCurrentPath.length === 0
                        ? "default"
                        : "pointer",
                  }}
                  onClick={() => {
                    const isFilesCategory = item.toLowerCase() === "files";
                    const isCurrentCategory = index === currentPath.length - 1;

                    if (
                      isFilesCategory &&
                      isCurrentCategory &&
                      filesCurrentPath.length > 0
                    ) {
                      // Clicking on "Files" when in a subfolder - just reset folder path
                      setFilesCurrentPath([]);
                    } else if (index !== currentPath.length - 1) {
                      // Clicking on a previous breadcrumb item - navigate back
                    
                      setFilesCurrentPath([]);
                      onNavigate(currentPath.slice(0, index + 1));
                    }
                  }}
                  aria-current={
                    index === currentPath.length - 1 &&
                      filesCurrentPath.length === 0
                      ? "page"
                      : undefined
                  }
                >
                  {item}
                </li>
              ))}
              {currentPath.length > 0 &&
                currentPath[currentPath.length - 1].toLowerCase() === "files" &&
                filesCurrentPath.map((folder, index) => (
                  <li
                    key={`folder-${index}`}
                    className={`breadcrumb-item aem-breadcrumb-item ${index === filesCurrentPath.length - 1 ? "active" : ""
                      }`}
                    onClick={() =>
                      index !== filesCurrentPath.length - 1 &&
                      setFilesCurrentPath((prev) => prev.slice(0, index + 1))
                    }
                    aria-current={
                      index === filesCurrentPath.length - 1 ? "page" : undefined
                    }
                  >
                    {folder}
                  </li>
                ))}
            </ol>
          </nav>
        </div>

        {/* Search and Filter (only show when in a category) */}
        {currentPath.length > 0 && (
          <div className="aem-category-header">
            <h5 className="aem-category-title">
              <i
                className={`fas ${getItemIconDetails(
                  { name: "", type: "category" },
                  currentPath[currentPath.length - 1]
                ).iconClass
                  }`}
                style={{
                  color: getItemIconDetails(
                    { name: "", type: "category" },
                    currentPath[currentPath.length - 1]
                  ).iconColor,
                }}
              ></i>
              {currentPath[currentPath.length - 1]}
            </h5>

            <div className="aem-category-controls">
              <div className="aem-search-container">
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={onSearchChange}
                />
                <button
                  className="aem-search-button"
                  onClick={() => onSearchChange({ target: { value: "" } })}
                  title={searchTerm ? "Clear search" : "Search"}
                >
                  <i
                    className={`fas ${searchTerm ? "fa-times" : "fa-search"}`}
                  ></i>
                </button>
              </div>

              <Form.Control
                as="select"
                value={filterOption}
                onChange={onFilterChange}
                className="aem-sort-select"
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="date-asc">Date (Oldest)</option>
                <option value="date-desc">Date (Newest)</option>
                <option value="size-asc">Size (Smallest)</option>
                <option value="size-desc">Size (Largest)</option>
              </Form.Control>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="aem-content">
          {contentLoading ? (
            <div className="aem-loading">
              <Spinner animation="border" role="status" variant="primary">
                <span className="sr-only">Loading...</span>
              </Spinner>
              <p className="mt-3 mb-0">Loading items...</p>
            </div>
          ) : currentPath.length === 0 ? (
            // Categories
            <div className="aem-categories-grid">
              {items.map((category) => {
                const { iconClass, iconColor } = getItemIconDetails(
                  category,
                  "category"
                );

                return (
                  <div
                    key={category.id}
                    className="aem-category-tile"
                    data-category={category.name}
                    onClick={() => handleCategoryClick(category.name)}
                  >
                    <div className="aem-category-icon-container">
                      <i
                        className={`fas ${iconClass} aem-category-icon`}
                        style={{ color: iconColor }}
                      ></i>
                    </div>
                    <div className="aem-category-name">{category.name}</div>
                  </div>
                );
              })}
            </div>
          ) : items.length === 0 ? (
            // Empty state
            <div className="aem-empty-state">
              <i className="fas fa-search"></i>
              <h5>No Items Found</h5>
              <p>
                {searchTerm
                  ? `No items matching "${searchTerm}" in ${currentPath[currentPath.length - 1]
                  }.`
                  : `There are no items in ${currentPath[currentPath.length - 1]
                  }.`}
              </p>
            </div>
          ) : (
            // Items list
            <div className="aem-items-list">
              {items.map((item) => {
                const { iconClass, iconColor } = getItemIconDetails(
                  item,
                  item.type
                );
                const alreadyInEndowment = isItemInEndowment(item);
                const partiallyInEndowment = isFolderPartiallyInEndowment(item);

                return (
                  <div
                    key={item.id}
                    className={`aem-item ${alreadyInEndowment ? "aem-item-already-added" : ""
                      } ${partiallyInEndowment ? "aem-item-partially-added" : ""
                      }`}
                    onClick={(e) => {
                      // Don't navigate if clicking on checkbox or view button
                      if (
                        e.target.closest(".aem-item-check") ||
                        e.target.closest(".aem-item-actions")
                      ) {
                        return;
                      }

                      e.preventDefault();
                      e.stopPropagation();

                      if (item.type === "folder") {
                        handleFilesFolderClick(item.name);
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (item.type === "file") {
                        handleViewItem(item);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="aem-item-check">
                      <Form.Check
                        type="checkbox"
                        id={`item-check-${item.id}`}
                        checked={alreadyInEndowment || isItemSelected(item)}
                        onChange={(e) =>
                          handleItemSelect(item, e.target.checked)
                        }
                        disabled={alreadyInEndowment && item.type !== "folder"}
                        ref={(input) => {
                          if (input && item.type === "folder") {
                            // Set indeterminate state for folders with partial selection
                            const filesInFolder = item.files || [];
                            const selectedFiles =
                              selectedItems[
                              currentPath[
                                currentPath.length - 1
                              ]?.toLowerCase()
                              ] || [];
                            const selectedCount = filesInFolder.filter((f) =>
                              selectedFiles.includes(f)
                            ).length;
                            input.indeterminate =
                              selectedCount > 0 &&
                              selectedCount < filesInFolder.length;
                          }
                        }}
                      />
                    </div>

                    <div className="aem-item-content">
                      <div className="aem-item-icon">
                        <i
                          className={`fas ${iconClass}`}
                          style={{ color: iconColor }}
                        ></i>
                      </div>
                      <div className="aem-item-details" style={{ flex: 1 }}>
                        <div className="aem-item-name" title={item.name}>
                          {removeTxtExtension(item.name)}
                        </div>
                        {/* Show metadata for files */}
                        {item.type !== "category" && (
                          <div
                            className="aem-item-metadata"
                            style={{
                              fontSize: "0.875rem",
                              color: "#6c757d",
                              marginTop: "2px",
                            }}
                          >
                            {item.type === "folder"
                              ? ` • ${formatDate(item.lastModified)}`
                              : `${formatFileSize(item.size)} • ${formatDate(
                                item.lastModified
                              )}`}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="aem-item-actions">
                      {item.type !== "folder" && (
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            handleViewItem(item);
                          }}
                          title="View item"
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                      )}
                    </div>

                    {alreadyInEndowment && (
                      <div className="aem-already-added-badge">
                        {item.type === "folder" ? "All Added" : "Already Added"}
                      </div>
                    )}
                    {partiallyInEndowment && (
                      <div className="aem-partially-added-badge">
                        Some Added
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-end w-100">
          <Button variant="secondary" onClick={onHide} disabled={savingItems}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={savingItems || countSelectedItems() === 0}
            className="ml-2"
          >
            {savingItems ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="mr-2"
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddToEndowmentModal;
